import React, { useEffect, useState } from "react";
import { Progress, Tag, Empty } from "antd";
import StatCard from "./StatCard";
import { getMyDashboardApi } from "../../services/api";

const STATUS_COLORS = { PENDING: "gold", APPROVED: "success", REJECTED: "error" };

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getMyDashboardApi();
        setData(res.data.data || {});
      } catch {
        setData({});
      }
    };
    load();
  }, []);

  const d = data || {};
  const target = d.monthlyTarget || 0;
  const thisMonth = d.vendorsThisMonth || 0;
  const targetPct = target > 0 ? Math.min(100, Math.round((thisMonth / target) * 100)) : 0;
  const recentVendors = d.recentVendors || [];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-sub">Your vendor onboarding progress and earnings</div>
        </div>
      </div>

      {/* VENDOR STATS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 16 }}>
        <StatCard label="TOTAL VENDORS ONBOARDED" value={d.totalVendorsOnboarded ?? 0} positive color="#111" />
        <StatCard label="APPROVED" value={d.approvedCount ?? 0} positive color="#43A047" />
        <StatCard label="PENDING" value={d.pendingCount ?? 0} positive color="#D4A017" />
        <StatCard label="REJECTED" value={d.rejectedCount ?? 0} positive={false} color="#E24B4A" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* MONTHLY TARGET */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", padding: 20 }}>
          <div className="section-title">Monthly Target</div>
          <Progress
            percent={targetPct}
            strokeColor="#0f6e56"
            trailColor="rgba(0,0,0,0.06)"
            format={() => `${thisMonth} / ${target}`}
          />
          <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
            {thisMonth >= target && target > 0
              ? "Target reached this month 🎉"
              : `${Math.max(0, target - thisMonth)} more vendor${target - thisMonth === 1 ? "" : "s"} to hit your target`}
          </div>
        </div>

        {/* COMMISSION */}
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", padding: 20 }}>
          <div className="section-title">Commission</div>
          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase" }}>Total Earned</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: "#1E88E5" }}>₹{d.totalCommissionEarned ?? 0}</div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase" }}>This Month</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: "#43A047" }}>₹{d.commissionThisMonth ?? 0}</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: "#888", marginTop: 12 }}>
            Current rate: <strong>{d.commissionRatePercent ?? 0}%</strong> of a vendor's plan fee, earned once when they're approved.
            Free-plan vendors earn ₹0.
          </div>
        </div>
      </div>

      {/* RECENT VENDORS */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", padding: 20 }}>
        <div className="section-title">Recent Vendors</div>
        {recentVendors.length === 0 && <Empty description="No vendors submitted yet" />}
        {recentVendors.map((v, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px 0",
              borderBottom: i < recentVendors.length - 1 ? "1px solid #eee" : "none",
            }}
          >
            <div>
              <div style={{ fontWeight: 500 }}>{v.name}</div>
              <div style={{ fontSize: 11, color: "#888" }}>{v.planName || "-"}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <Tag color={STATUS_COLORS[v.onboardingStatus] || "default"}>{v.onboardingStatus}</Tag>
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                {v.commissionAmount != null ? `₹${v.commissionAmount} earned` : "—"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
